import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from '@forgeglobal/design-framework';
import { useTheme } from '@mui/material/styles';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import { useStyles } from './style';

export type ModalType = 'error' | 'success' | 'info' | 'warning';

export interface ConfirmationModalProps {
  open: boolean;
  title: string;
  content: string;
  modalType: ModalType;
  onClose: () => void;
  confirmButtonText?: string;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  open,
  title,
  content,
  modalType,
  onClose,
  confirmButtonText = 'OK',
}) => {
  const classes = useStyles();
  const theme = useTheme();

  const getIconColor = () => {
    switch (modalType) {
      case 'error':
        return theme.palette.error.main;
      case 'success':
        return theme.palette.success?.main || '#2e7d32';
      case 'info':
        return theme.palette.info?.main || theme.palette.primary.main;
      case 'warning':
        return theme.palette.warning?.main || '#ed6c02';
      default:
        return theme.palette.primary.main;
    }
  };

  const getIcon = () => {
    const iconColor = getIconColor();
    switch (modalType) {
      case 'error':
        return (
          <ErrorIcon className={classes.icon} style={{ color: iconColor }} />
        );
      case 'success':
        return (
          <CheckCircleIcon
            className={classes.icon}
            style={{ color: iconColor }}
          />
        );
      case 'info':
        return (
          <InfoIcon className={classes.icon} style={{ color: iconColor }} />
        );
      case 'warning':
        return (
          <WarningIcon className={classes.icon} style={{ color: iconColor }} />
        );
      default:
        return (
          <InfoIcon className={classes.icon} style={{ color: iconColor }} />
        );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className={classes.dialog}
      disableEnforceFocus>
      <DialogTitle onClose={onClose}>{title}</DialogTitle>
      <DialogContent className={classes.wrapper}>
        <Box className={classes.content}>
          <Box className={classes.iconContainer}>{getIcon()}</Box>
          <Typography variant="body1" className={classes.contentText}>
            {content}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button variant="outlined" onClick={onClose}>
          {confirmButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationModal;
